// The site catalogs the issue form picks from, and a floor's plan. They change
// rarely, so they stay fresh for five minutes and skip refetch-on-focus.

import { useQuery } from '@tanstack/react-query';
import { useClientApis } from '../../hooks/ClientApisProvider.js';
import { issuesKeys } from '../queryKeys.js';

const CATALOG = { staleTime: 5 * 60_000, refetchOnWindowFocus: false } as const;

export function useSiteUsers(siteId: string | null) {
  const { fetchSiteUsers } = useClientApis().issues;
  return useQuery({
    queryKey: issuesKeys.users(siteId),
    queryFn: ({ signal }) => fetchSiteUsers(siteId!, signal),
    enabled: !!siteId,
    ...CATALOG,
  });
}

export function useSiteAreas(siteId: string | null) {
  const { fetchAreas } = useClientApis().issues;
  return useQuery({
    queryKey: issuesKeys.areas(siteId),
    queryFn: ({ signal }) => fetchAreas(siteId!, signal),
    enabled: !!siteId,
    ...CATALOG,
  });
}

/** The site's full profession catalog — every trade, not just those on issues. */
export function useSiteProfessions(siteId: string | null) {
  const { fetchSiteProfessions } = useClientApis().issues;
  return useQuery({
    queryKey: issuesKeys.professions(siteId),
    queryFn: ({ signal }) => fetchSiteProfessions(siteId!, signal),
    enabled: !!siteId,
    ...CATALOG,
  });
}

/** A floor's plan SVG; null when the project has none. */
export function useFloorViewSvg(siteId: string | null, floorId: string) {
  const { fetchFloorViewSvg } = useClientApis().storage;
  return useQuery({
    queryKey: issuesKeys.floorView(siteId, floorId),
    queryFn: ({ signal }) => fetchFloorViewSvg(siteId!, floorId, signal),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
  });
}
